/**
 * RoyETH Carry-Trade Backtest Engine
 *
 * Models the actual RoyETH balance sheet:
 *   1. Deposit 1 wstETH as collateral on Aave  (earns supply APY)
 *   2. Borrow LTV × P₀ USDC                     (accrues borrow interest)
 *   3. Deposit that USDC into DSV (srRoyUSDC)    (earns DSV senior yield)
 *
 * NAV(t) = wstETH_price(t) × supplyCompound(t)          ← collateral value
 *        + LTV × P₀ × (1 + dsvCumReturn(t))              ← DSV deposit value
 *        − LTV × P₀ × borrowCompound(t)                  ← USDC debt
 *
 * ETH-normalized view uses on-chain wstETH/ETH exchange rate
 * (compounded from DeFi Llama staking yield) instead of CoinGecko
 * price ratios, eliminating snapshot-timing noise.
 */

const DEFAULTS = {
  aaveLTV: 39,            // percent (39 = 39%)
  useHistoricRates: true,
  stakingYieldOverride: 2.7,   // % annualized
  aaveSupplyOverride: 0.04,    // % annualized
  usdcBorrowOverride: 5.6,     // % annualized
  ltvMode: 'fixed',            // 'fixed' | 'dynamic'
  maxDsvRedemption: 3,         // max % of DSV position redeemable per day
  harvestEnabled: true,        // periodically convert excess USDC to wstETH collateral
  harvestIntervalDays: 7,      // days between harvests
};

/**
 * @param {Object} opts
 * @param {Object} opts.dsvResult      – output of dsv-engine simulate()
 * @param {Object} opts.rateData       – { meta, data: { "YYYY-MM-DD": { stakingYield, aaveSupplyYield, usdcBorrowRate, ethPrice, wstEthPrice } } }
 * @param {Object} [opts.config]       – overrides for DEFAULTS
 */
export function simulateRoyETH({ dsvResult, rateData, config: userConfig }) {
  const config = { ...DEFAULTS, ...userConfig };
  const ltv = config.aaveLTV / 100;

  const dates = dsvResult.dates;
  if (!dates || dates.length < 2) {
    return emptyResult();
  }

  const dsvCum = dsvResult.aggregate.dsvCumReturn;
  const rateMap = (rateData && rateData.data) || {};

  // ── Initial prices ──
  const r0 = findRates(rateMap, dates[0], config);
  const P0 = r0.wstEthPrice;          // wstETH price at inception (USD)
  const ethPrice0 = r0.ethPrice;
  const initRatio = ethPrice0 > 0 ? P0 / ethPrice0 : 1.22; // wstETH/ETH at inception

  if (config.ltvMode === 'dynamic') {
    return simulateDynamic({ dates, dsvCum, rateMap, config, ltv, P0, ethPrice0, initRatio });
  }

  // ── Fixed LTV mode (existing analytical path) ──

  // ── Balance-sheet accumulators ──
  let stakeCompound = 1.0;            // on-chain wstETH/ETH ratio growth (from staking yield)
  let supplyCompound = 1.0;           // compound factor for Aave supply APY on collateral
  let borrowCompound = 1.0;           // compound factor for USDC borrow cost

  // ── Harvest state (explicit USD tracking when harvest enabled) ──
  const harvestEnabled = config.harvestEnabled;
  const harvestInterval = config.harvestIntervalDays || 7;
  let collateralUnits = 1.0;
  let debtUSD = ltv * P0;
  let dsvUSD = ltv * P0;
  let daysSinceHarvest = 0;
  const harvests = [];

  // ── Output series (all indexed so day-0 = 1.0) ──
  const royethNav = [1.0];            // NAV / P0 (USD-denominated)
  const ethIndexed = [1.0];           // ETH price / ETH price at inception
  const wstEthIndexed = [1.0];        // wstETH price / wstETH price at inception (from CoinGecko)
  const dsvSrIndexed = [1.0];
  const dailyApy = [0];               // instantaneous annualized yield (%)
  const components = { staking: [0], supply: [0], carry: [0], dsvYield: [0], borrowRate: [0] };

  // ETH-normalized series (smooth, using on-chain staking rate)
  const royethVsEth = [1.0];
  const wstEthVsEth = [1.0];

  for (let i = 1; i < dates.length; i++) {
    const calDays = (Date.parse(dates[i]) - Date.parse(dates[i - 1])) / 86400000;
    const dt = calDays > 0 ? calDays : 1;

    // Look up rates for this day
    const rates = findRates(rateMap, dates[i], config);
    const asup = (config.useHistoricRates ? rates.aaveSupplyYield : config.aaveSupplyOverride) / 100;
    const ubr = (config.useHistoricRates ? rates.usdcBorrowRate : config.usdcBorrowOverride) / 100;
    const sk = (config.useHistoricRates ? rates.stakingYield : config.stakingYieldOverride) / 100;

    // Compound on-chain staking yield (smooth wstETH/ETH ratio)
    const stakingDaily = Math.pow(1 + sk, dt / 365) - 1;
    stakeCompound *= (1 + stakingDaily);

    // Compound supply APY on collateral
    const supplyDaily = Math.pow(1 + asup, dt / 365) - 1;
    supplyCompound *= (1 + supplyDaily);

    // Compound borrow cost on USDC debt
    const borrowDaily = Math.pow(1 + ubr, dt / 365) - 1;
    borrowCompound *= (1 + borrowDaily);

    // Track explicit USD positions (for harvest mode)
    if (harvestEnabled) {
      debtUSD *= (1 + borrowDaily);
      const dsvPrevVal = 1 + dsvCum[i - 1];
      const dsvDailyRet = dsvPrevVal > 0 ? (1 + dsvCum[i]) / dsvPrevVal - 1 : 0;
      dsvUSD *= (1 + dsvDailyRet);
      daysSinceHarvest += dt;

      // Harvest: convert excess USDC to wstETH, re-lever
      if (daysSinceHarvest >= harvestInterval) {
        const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
        const excessUSD = dsvUSD - debtUSD;
        if (excessUSD > 0) {
          const wstEthBought = excessUSD / wstEthPrice;
          const newBorrow = excessUSD * ltv;
          collateralUnits += wstEthBought;
          debtUSD += newBorrow;
          dsvUSD = debtUSD;
          harvests.push({ date: dates[i], excessUSD, wstEthBought, newBorrow });
        }
        daysSinceHarvest = 0;
      }
    }

    // ETH price (indexed) — from CoinGecko, single series (low noise)
    const ethIdx = (ethPrice0 > 0 && rates.ethPrice > 0)
      ? rates.ethPrice / ethPrice0
      : ethIndexed[i - 1];

    // wstETH price (indexed) — from CoinGecko for USD view
    const wstIdx = (P0 > 0 && rates.wstEthPrice > 0)
      ? rates.wstEthPrice / P0
      : wstEthIndexed[i - 1];

    // DSV deposit value (indexed to initial USDC amount)
    const dsvValue = 1 + dsvCum[i];

    // ── USD NAV / P₀ (balance sheet) ──
    let nav;
    if (harvestEnabled) {
      const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
      const collateralValueUSD = collateralUnits * wstEthPrice * supplyCompound;
      const navUSD = collateralValueUSD + dsvUSD - debtUSD;
      nav = navUSD / P0;
    } else {
      nav = wstIdx * supplyCompound + ltv * (dsvValue - borrowCompound);
    }
    royethNav.push(nav);
    ethIndexed.push(ethIdx);
    wstEthIndexed.push(wstIdx);
    dsvSrIndexed.push(dsvValue);

    // ── ETH-normalized (smooth, using on-chain staking rate) ──
    wstEthVsEth.push(stakeCompound);

    if (harvestEnabled) {
      const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
      const collateralValueUSD = collateralUnits * wstEthPrice * supplyCompound;
      const navUSD = collateralValueUSD + dsvUSD - debtUSD;
      const ethPrice = (rates.ethPrice > 0) ? rates.ethPrice : ethPrice0;
      const initNavEth = P0 / ethPrice0;
      royethVsEth.push(initNavEth > 0 ? (navUSD / ethPrice) / initNavEth : 1.0);
    } else {
      const carryInEth = ethIdx > 0
        ? ltv * initRatio * (dsvValue - borrowCompound) / ethIdx
        : 0;
      royethVsEth.push(stakeCompound * supplyCompound + carryInEth);
    }

    // ── Instantaneous annualized yield for display ──
    const dsvPrev = 1 + dsvCum[i - 1];
    const dsvDailyRet = dsvPrev > 0 ? (1 + dsvCum[i]) / dsvPrev - 1 : 0;
    const srYieldAnn = (Math.pow(1 + dsvDailyRet, 365 / dt) - 1) * 100;
    const stakingComp = sk * 100;
    const supplyComp = asup * 100;
    const carryComp = ltv * (srYieldAnn - ubr * 100);

    dailyApy.push(stakingComp + supplyComp + carryComp);
    components.staking.push(stakingComp);
    components.supply.push(supplyComp);
    components.carry.push(carryComp);
    components.dsvYield.push(srYieldAnn);
    components.borrowRate.push(ubr * 100);
  }

  // ── Summary stats (all ETH-normalized) ──
  const totalDays = (Date.parse(dates[dates.length - 1]) - Date.parse(dates[0])) / 86400000;
  const lastVsEth = royethVsEth[royethVsEth.length - 1];
  const totalReturn = lastVsEth - 1;
  const annualizedReturn = totalDays > 0
    ? Math.pow(lastVsEth, 365 / totalDays) - 1
    : 0;
  const n = Math.max(dailyApy.length - 1, 1);
  const avgApy = dailyApy.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgDsvYield = components.dsvYield.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgBorrowRate = components.borrowRate.slice(1).reduce((s, v) => s + v, 0) / n;

  const wstEthReturn = wstEthVsEth[wstEthVsEth.length - 1] - 1;
  const vsWstEthTotal = totalReturn - wstEthReturn;
  // Annualized excess over wstETH: compound ratio of royethVsEth / wstEthVsEth
  const lastRoyWst = lastVsEth / wstEthVsEth[wstEthVsEth.length - 1];
  const vsWstEthAnnualized = totalDays > 0
    ? Math.pow(lastRoyWst, 365 / totalDays) - 1
    : 0;

  return {
    dates,
    royethNav,
    royethVsEth,
    wstEthVsEth,
    ethIndexed,
    wstEthIndexed,
    dsvSrIndexed,
    dailyApy,
    components,
    actualLTV: null,  // not applicable in fixed mode
    harvests,
    summary: {
      totalReturn,
      annualizedReturn,
      days: totalDays,
      avgApy,
      avgDsvYield,
      avgBorrowRate,
      wstEthReturn,
      vsWstEth: vsWstEthTotal,
      vsWstEthAnnualized,
    },
  };
}

/**
 * Dynamic LTV simulation — tracks explicit USD amounts and rebalances daily.
 * When ETH rises, borrows more USDC and deposits into DSV (instant).
 * When ETH falls, redeems DSV to repay debt (capped by maxDsvRedemption per day).
 */
function simulateDynamic({ dates, dsvCum, rateMap, config, ltv, P0, ethPrice0, initRatio }) {
  const maxRedeemPct = config.maxDsvRedemption / 100;

  // ── Explicit USD state ──
  let collateralUnits = 1.0;
  let debtUSD = ltv * P0;
  let dsvUSD = ltv * P0;

  // ── Harvest state ──
  const harvestEnabled = config.harvestEnabled;
  const harvestInterval = config.harvestIntervalDays || 7;
  let daysSinceHarvest = 0;
  const harvests = [];

  // ── Accumulators for staking/supply (same as fixed mode) ──
  let stakeCompound = 1.0;
  let supplyCompound = 1.0;

  // ── Output series ──
  const royethNav = [1.0];
  const ethIndexed = [1.0];
  const wstEthIndexed = [1.0];
  const dsvSrIndexed = [1.0];
  const dailyApy = [0];
  const components = { staking: [0], supply: [0], carry: [0], dsvYield: [0], borrowRate: [0] };
  const royethVsEth = [1.0];
  const wstEthVsEth = [1.0];
  const actualLTV = [ltv * 100]; // day-0 = target LTV (in %)

  for (let i = 1; i < dates.length; i++) {
    const calDays = (Date.parse(dates[i]) - Date.parse(dates[i - 1])) / 86400000;
    const dt = calDays > 0 ? calDays : 1;

    const rates = findRates(rateMap, dates[i], config);
    const asup = (config.useHistoricRates ? rates.aaveSupplyYield : config.aaveSupplyOverride) / 100;
    const ubr = (config.useHistoricRates ? rates.usdcBorrowRate : config.usdcBorrowOverride) / 100;
    const sk = (config.useHistoricRates ? rates.stakingYield : config.stakingYieldOverride) / 100;

    // Compound on-chain staking yield
    const stakingDaily = Math.pow(1 + sk, dt / 365) - 1;
    stakeCompound *= (1 + stakingDaily);

    // Compound supply APY on collateral
    const supplyDaily = Math.pow(1 + asup, dt / 365) - 1;
    supplyCompound *= (1 + supplyDaily);

    // Step 1: Accrue interest/yield on existing positions
    const borrowDailyRate = Math.pow(1 + ubr, dt / 365) - 1;
    debtUSD *= (1 + borrowDailyRate);

    const dsvPrev = 1 + dsvCum[i - 1];
    const dsvDailyRet = dsvPrev > 0 ? (1 + dsvCum[i]) / dsvPrev - 1 : 0;
    dsvUSD *= (1 + dsvDailyRet);

    // Get ETH price for this day (needed by harvest and collateral calc)
    const ethPrice = (rates.ethPrice > 0) ? rates.ethPrice : ethPrice0;

    // ── Harvest: convert excess USDC to wstETH, re-lever ──
    if (harvestEnabled) {
      daysSinceHarvest += dt;
      if (daysSinceHarvest >= harvestInterval) {
        const excessUSD = dsvUSD - debtUSD;
        if (excessUSD > 0) {
          const wstEthPriceForHarvest = ethPrice * stakeCompound * initRatio;
          const wstEthBought = excessUSD / wstEthPriceForHarvest;
          const newBorrow = excessUSD * ltv;
          collateralUnits += wstEthBought;
          debtUSD += newBorrow;
          dsvUSD = debtUSD;
          harvests.push({ date: dates[i], excessUSD, wstEthBought, newBorrow });
        }
        daysSinceHarvest = 0;
      }
    }

    // Step 2: Compute current collateral value & actual LTV
    // Derive wstETH/USD from ETH/USD × on-chain wstETH/ETH ratio
    // to avoid CoinGecko snapshot timing mismatch between the two price feeds
    const wstEthPriceDerived = ethPrice * stakeCompound * initRatio;
    const collateralUSD = collateralUnits * wstEthPriceDerived * supplyCompound;
    const currentLTV = collateralUSD > 0 ? debtUSD / collateralUSD : 0;

    // Step 3: Rebalance toward targetLTV
    const desiredDebt = ltv * collateralUSD;

    if (currentLTV < ltv) {
      // ETH went up — LTV too low — borrow more and deposit into DSV (instant)
      const borrowMore = desiredDebt - debtUSD;
      debtUSD += borrowMore;
      dsvUSD += borrowMore;
    } else if (currentLTV > ltv) {
      // ETH went down — LTV too high — redeem DSV to repay (capped)
      const repayAmt = debtUSD - desiredDebt;
      const maxRedeem = maxRedeemPct * dsvUSD;
      const actualRepay = Math.min(repayAmt, maxRedeem);
      dsvUSD -= actualRepay;
      debtUSD -= actualRepay;
    }

    // Record actual LTV after rebalancing (in %)
    const ltvAfter = collateralUSD > 0 ? (debtUSD / collateralUSD) * 100 : 0;
    actualLTV.push(ltvAfter);

    // Step 4: Compute NAV
    const navUSD = collateralUSD + dsvUSD - debtUSD;

    // ── Index series ──
    const ethIdx = (ethPrice0 > 0 && rates.ethPrice > 0)
      ? rates.ethPrice / ethPrice0
      : ethIndexed[i - 1];

    royethNav.push(navUSD / P0);
    ethIndexed.push(ethIdx);
    wstEthIndexed.push(wstEthPriceDerived / P0);
    dsvSrIndexed.push(1 + dsvCum[i]);

    // ETH-normalized views
    wstEthVsEth.push(stakeCompound);

    // RoyETH vs ETH: navUSD expressed in ETH, normalized to initial ETH-denominated NAV
    // ethPrice already derived above (Step 2)
    const initNavEth = P0 / ethPrice0; // initial NAV in ETH terms
    royethVsEth.push(initNavEth > 0 ? (navUSD / ethPrice) / initNavEth : 1.0);

    // ── Instantaneous annualized yield for display ──
    const srYieldAnn = (Math.pow(1 + dsvDailyRet, 365 / dt) - 1) * 100;
    const effectiveLtv = collateralUSD > 0 ? debtUSD / collateralUSD : ltv;
    const stakingComp = sk * 100;
    const supplyComp = asup * 100;
    const carryComp = effectiveLtv * (srYieldAnn - ubr * 100);

    dailyApy.push(stakingComp + supplyComp + carryComp);
    components.staking.push(stakingComp);
    components.supply.push(supplyComp);
    components.carry.push(carryComp);
    components.dsvYield.push(srYieldAnn);
    components.borrowRate.push(ubr * 100);
  }

  // ── Summary stats (all ETH-normalized) ──
  const totalDays = (Date.parse(dates[dates.length - 1]) - Date.parse(dates[0])) / 86400000;
  const lastVsEth = royethVsEth[royethVsEth.length - 1];
  const totalReturn = lastVsEth - 1;
  const annualizedReturn = totalDays > 0
    ? Math.pow(lastVsEth, 365 / totalDays) - 1
    : 0;
  const n = Math.max(dailyApy.length - 1, 1);
  const avgApy = dailyApy.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgDsvYield = components.dsvYield.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgBorrowRate = components.borrowRate.slice(1).reduce((s, v) => s + v, 0) / n;

  const wstEthReturn = wstEthVsEth[wstEthVsEth.length - 1] - 1;
  const vsWstEthTotal = totalReturn - wstEthReturn;
  const lastRoyWst = lastVsEth / wstEthVsEth[wstEthVsEth.length - 1];
  const vsWstEthAnnualized = totalDays > 0
    ? Math.pow(lastRoyWst, 365 / totalDays) - 1
    : 0;

  return {
    dates,
    royethNav,
    royethVsEth,
    wstEthVsEth,
    ethIndexed,
    wstEthIndexed,
    dsvSrIndexed,
    dailyApy,
    components,
    actualLTV,
    harvests,
    summary: {
      totalReturn,
      annualizedReturn,
      days: totalDays,
      avgApy,
      avgDsvYield,
      avgBorrowRate,
      wstEthReturn,
      vsWstEth: vsWstEthTotal,
      vsWstEthAnnualized,
    },
  };
}

// ── Helpers ──

function findRates(rateMap, date, config) {
  const entry = rateMap[date];
  if (entry) return entry;

  const d = new Date(date + "T00:00:00Z");
  for (let k = 1; k <= 7; k++) {
    d.setUTCDate(d.getUTCDate() - 1);
    const key = d.toISOString().slice(0, 10);
    if (rateMap[key]) return rateMap[key];
  }

  return {
    stakingYield: config.stakingYieldOverride,
    aaveSupplyYield: config.aaveSupplyOverride,
    usdcBorrowRate: config.usdcBorrowOverride,
    ethPrice: 0,
    wstEthPrice: 0,
  };
}

function emptyResult() {
  return {
    dates: [],
    royethNav: [],
    royethVsEth: [],
    wstEthVsEth: [],
    ethIndexed: [],
    wstEthIndexed: [],
    dsvSrIndexed: [],
    dailyApy: [],
    components: { staking: [], supply: [], carry: [], dsvYield: [], borrowRate: [] },
    actualLTV: null,
    harvests: [],
    summary: {
      totalReturn: 0, annualizedReturn: 0, days: 0, avgApy: 0,
      avgDsvYield: 0, avgBorrowRate: 0,
      wstEthReturn: 0, vsWstEth: 0, vsWstEthAnnualized: 0,
    },
  };
}
